import { type UseMutationOptions, type UseQueryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import { RESTRICTED_CONTENT_TYPES } from "@equidefi/shared";
import type { DTO } from "@equidefi/shared/types";
import AWSClient from "../clients/AWSClient";
import AccreditationClient from "../clients/AccreditationClient";
import type { AddAccreditationFileProps } from "../components/accreditation/AddAccreditationFile";
import { vaultKeys } from "./queryKeys";
import { useApi } from "./useApi";

type UpdateVaultMutationOptions = UseMutationOptions<unknown, unknown, Record<string, unknown>>;
export const useUpdateVault = (investmentId: string, options: UpdateVaultMutationOptions = {}) => {
  const accreditation = useApi(AccreditationClient);

  return useMutation(async (data) => accreditation.updateVault(investmentId, data), options);
};

export const useStoreAccreditationFile = (investmentId: string, options: UseMutationOptions = {}) => {
  const accreditation = useApi(AccreditationClient);

  return useMutation(
    async (data: Omit<Parameters<typeof accreditation.storeAccreditationFile>[0], "investmentId">) =>
      accreditation.storeAccreditationFile({ ...data, investmentId }),
    options,
  );
};

export const useUpdateAccreditationFile = (vaultId: string, fileId: string, options: UseMutationOptions = {}) => {
  const accreditation = useApi(AccreditationClient);

  return useMutation(
    async (data: Parameters<typeof accreditation.updateAccreditationFile>[2]) =>
      accreditation.updateAccreditationFile(vaultId, fileId, data),
    options,
  );
};

export const useRemoveAccreditationFile = (vaultId: string, options = {}) => {
  const accreditation = useApi(AccreditationClient);

  return useMutation(async (fileId: string) => accreditation.removeAccreditationFile(vaultId, fileId), options);
};

export const usePresignedUrl = (investmentId: string, options = {}) => {
  const accreditation = useApi(AccreditationClient);

  return useMutation(
    async (data: { filename: string; fileExtension: string }) =>
      accreditation.getPresignedUrl(investmentId, data.filename, data.fileExtension),
    options,
  );
};

export const useVerificationReport = (investmentId: string, options = {}) => {
  const accreditation = useApi(AccreditationClient);

  return useMutation(
    async (data: { filename: string; extension: string }) =>
      accreditation.submitVerificationReport(investmentId, data.filename, data.extension),
    options,
  );
};

export const useSubmitPreApproved = (investmentId: string, options = {}) => {
  const accreditation = useApi(AccreditationClient);

  return useMutation(async () => accreditation.submitPreApproved(investmentId), options);
};

export const useGetAccreditationFiles = (vaultId?: string, options: UseQueryOptions<DTO.AccreditationFile[]> = {}) => {
  const accreditation = useApi(AccreditationClient);

  return useQuery<DTO.AccreditationFile[]>({
    queryKey: vaultKeys.files(vaultId as string),
    queryFn: () => accreditation.getAccreditationFiles(vaultId as string),
    enabled: !!vaultId,
    ...options,
  });
};

export const useGetDocumentURLs = (vaultId?: string, options = {}) => {
  const accreditation = useApi(AccreditationClient);

  return useQuery(vaultKeys.downloadUrls(vaultId as string), () => accreditation.getDownloadUrls(vaultId as string), {
    enabled: !!vaultId,
    ...options,
  });
};

type PresignedUrlMutation = {
  mutateAsync: (data: { filename: string; fileExtension: string }) => Promise<{ signedUrl: string; url: string }>;
  reset: () => void;
  isLoading: boolean;
};

export type UseHandleAccreditationUploadProps = {
  investmentId?: string;
  presignedUrl?: PresignedUrlMutation;
  storeFile: {
    mutateAsync: (data: {
      name: string;
      type: string;
      url: string;
      filetype?: string;
    }) => Promise<unknown>;
    reset: () => void;
    isLoading: boolean;
  };
  onError?: (error: Error) => void;
  onDone?: () => Promise<void>;
};

export type UseHandleAccreditationUploadReturn = {
  isUploading: boolean;
  upload: (file: File, type: string) => Promise<DTO.AccreditationFile | undefined>;
  onSubmit: AddAccreditationFileProps["onSubmit"];
};

export const useHandleAccreditationUpload = ({
  investmentId,
  presignedUrl,
  storeFile,
  onError,
  onDone = async () => {},
}: UseHandleAccreditationUploadProps): UseHandleAccreditationUploadReturn => {
  const awsClient = useApi(AWSClient);
  const investmentPresignedUrl = usePresignedUrl(investmentId as string);
  const getPresignedUrl = presignedUrl ?? investmentPresignedUrl;

  const [fileLoading, setFileLoading] = useState(false);
  const isUploading = useMemo(
    () => fileLoading || getPresignedUrl.isLoading || storeFile.isLoading,
    [getPresignedUrl.isLoading, storeFile.isLoading, fileLoading],
  );

  const upload = useCallback(
    async (file: File, type: string) => {
      try {
        setFileLoading(true);
        const extension = file.name.split(".").pop()?.toLowerCase() || "";
        if (!Object.keys(RESTRICTED_CONTENT_TYPES).includes(extension)) {
          throw new Error(`Filetype "${extension}" is not supported`);
        }

        const response = await getPresignedUrl.mutateAsync({
          filename: file.name,
          fileExtension: extension,
        });

        await awsClient.s3Upload(response.signedUrl, file, RESTRICTED_CONTENT_TYPES[extension]);

        const result = await storeFile.mutateAsync({
          name: file.name,
          type,
          url: response.url,
          filetype: file.type,
        });

        return result;
      } catch (e) {
        onError?.(e as Error);
        console.error(e);
      } finally {
        setFileLoading(false);

        getPresignedUrl.reset();
        storeFile.reset();
      }
    },
    [investmentId, awsClient.s3Upload, getPresignedUrl, onDone, onError, storeFile],
  );

  const handleFileUpload: AddAccreditationFileProps["onSubmit"] = async (data) => {
    setFileLoading(true);

    await Promise.allSettled(data.files.map((f) => upload(f, data.type))).finally(() => {
      onDone?.();
      setFileLoading(false);
    });
  };

  return {
    isUploading,
    upload,
    onSubmit: handleFileUpload,
  };
};
